import { Money, StandardEventProperties, formatDate, formatMoney, useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Card, Heading, Separator } from '@betterment/samba-react';
import { SystemStyleObject } from '@betterment/samba-styled-system/types';
import { SambaAutoLink } from '@betterment/shared-components';

import { RecurringScheduledTransactionsDocument } from './graphql/recurringScheduledTransactions.generated';
import { NoTransactionsRow } from './NoTransactionsRow';
import { TransactionRow } from './TransactionRow';

import { trackClick } from '../../features/track';
import {
  LegacyTransferType,
  PayrollContributionConfigValueAmount,
  PayrollContributionConfigValuePercent,
  RecurringTransactionConfigStatus,
  ThemedAsset,
} from '../../graphql/schema';
import { CompactCardLoader } from '../../Shared/components-v2/shimmer-loaders/compact-card-loader';
import { urlBuilder } from '../../Shared/urlBuilder';

type ScheduledTransaction = {
  id: string;
  icon: ThemedAsset;
  transactionUrl: string;
  eventProperties: StandardEventProperties;
  amount?: string;
  destinationAccountName: string;
  transactionDescription: string;
  nextTransactionDate?: string;
  isReadOnly: boolean;
  status?: RecurringTransactionConfigStatus;
};

type Props = {
  css?: SystemStyleObject;
};

interface RecurringTransactionConfig {
  statuses: Array<RecurringTransactionConfigStatus>;
  nextTransactionDate: Date;
  sourceName: string;
  amount: Money;
  destinationName: string;
  icon: ThemedAsset;
  id: string;
  legacyTransferType: LegacyTransferType;
  legacyId?: number | null;
  legacyUuid?: string | null;
  isEditable: boolean;
}

interface Five29Account {
  icon: ThemedAsset;
  __typename: string;
}

interface ManagedSubAccount {
  icon: ThemedAsset;
  __typename: string;
}

interface PayrollContributionConfig {
  id: string;
  legacyId: number;
  destinationName: string;
  account: Five29Account | ManagedSubAccount;
  value: PayrollContributionConfigValueAmount | PayrollContributionConfigValuePercent;
}

const getTransactionUrlByTransferType = (configuration: RecurringTransactionConfig): string => {
  let transactionUrl = '';

  switch (configuration.legacyTransferType) {
    case LegacyTransferType.Deposit:
      if (configuration.legacyId) {
        transactionUrl = urlBuilder.appAutomaticDepositConfigEdit.absolute({
          automaticDepositConfigId: configuration.legacyId,
        });
      }
      break;
    case LegacyTransferType.Withdrawal:
      if (configuration.legacyId) {
        transactionUrl = urlBuilder.appAutomaticWithdrawalConfigEdit.absolute({
          automaticWithdrawalConfigId: configuration.legacyId,
        });
      }
      break;
    case LegacyTransferType.Transfer:
      if (configuration.legacyUuid) {
        transactionUrl = urlBuilder.appRecurringTransferConfigEdit.absolute({
          recurringTransferConfigId: configuration.legacyUuid,
        });
      }
      break;
  }

  if (transactionUrl === '') throw new Error('cannot get transaction URL from unknown LegacyTransferType/ID');

  return transactionUrl;
};

const getTransactionUrlByContributionType = (configuration: PayrollContributionConfig): string => {
  return configuration.account.__typename === 'Five29Account'
    ? urlBuilder.appPayrollDeductionSetup.absolute({ return_to: window.location.pathname })
    : urlBuilder.appDeferralSetting.absolute({
        legalAccountId: configuration.legacyId,
        return_to: window.location.pathname,
      });
};

const getDescriptionFromContribution = (configuration: PayrollContributionConfig): string => {
  const amount =
    configuration.value.__typename === 'PayrollContributionConfigValuePercent'
      ? `${configuration.value.percent}%`
      : formatMoney(configuration.value.amount);

  return `${amount} per pay period`;
};

const convertFromTransaction = (configuration: RecurringTransactionConfig): ScheduledTransaction => {
  return {
    id: configuration.id,
    icon: configuration.icon,
    destinationAccountName: configuration.destinationName,
    amount: formatMoney(configuration.amount),
    transactionDescription: `From ${configuration.sourceName}`,
    nextTransactionDate: formatDate(configuration.nextTransactionDate, 'shortOrdinalShortMonth'),
    transactionUrl: getTransactionUrlByTransferType(configuration),
    status: configuration.statuses[0],
    isReadOnly: !configuration.isEditable,
  } as ScheduledTransaction;
};

const convertFromContribution = (configuration: PayrollContributionConfig): ScheduledTransaction => {
  return {
    id: configuration.id,
    icon: configuration.account.icon,
    destinationAccountName: configuration.destinationName,
    transactionDescription: getDescriptionFromContribution(configuration),
    transactionUrl: getTransactionUrlByContributionType(configuration),
  } as ScheduledTransaction;
};

const ScheduledTransactionsCardComponent = (props: Props) => {
  const { data } = useGraphQLQuery(RecurringScheduledTransactionsDocument, { throwOnError: true });

  if (!data) return <CompactCardLoader />;

  if (!data.envelopes.length) return null;

  const { css: cssProp, ...restProps } = props;

  const { payrollContributionConfigs, recurringTransactionConfigs } = data;
  const scheduledTransactions = [
    ...payrollContributionConfigs.map(convertFromContribution),
    ...recurringTransactionConfigs.map(convertFromTransaction),
  ];
  const slicedTransactions = scheduledTransactions.slice(0, 3);

  return (
    <Card css={{ ...cssProp }} {...restProps} data-testid="scheduled-transactions-card">
      <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
        <Heading as="h2" variant="title200">
          Scheduled transactions
        </Heading>
        <SambaAutoLink
          to={urlBuilder.getPendingTransfers.absolute()}
          aria-label="See details"
          role="link"
          onClick={() => trackClick({ name: 'SeeDetail', module: 'ScheduledTransactions', location: 'Summary' })}
        >
          See details
        </SambaAutoLink>
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      {scheduledTransactions.length > 0 ? (
        <Box css={{ paddingY: 'space.150' }}>
          {slicedTransactions.map(transaction => (
            <TransactionRow
              key={transaction.id}
              icon={transaction.icon}
              transactionUrl={transaction.transactionUrl}
              eventProperties={transaction.eventProperties}
              amount={transaction.amount}
              destinationAccountName={transaction.destinationAccountName}
              transactionDescription={transaction.transactionDescription}
              nextTransactionDate={transaction.nextTransactionDate}
              status={transaction.status}
              isReadOnly={transaction.isReadOnly}
            />
          ))}
          {scheduledTransactions.length > 3 && (
            <Box css={{ paddingX: 'space.300', paddingY: 'space.150' }}>
              <SambaAutoLink
                to={urlBuilder.getPendingTransfers.absolute()}
                aria-label="See all pending transactions"
                role="link"
                onClick={() => trackClick({ name: 'SeeDetail', module: 'ScheduledTransactions', location: 'Summmary' })}
              >
                {`See ${scheduledTransactions.length - 3} more`}
              </SambaAutoLink>
            </Box>
          )}
        </Box>
      ) : (
        <NoTransactionsRow />
      )}
    </Card>
  );
};

export const ScheduledTransactionsCard = Object.assign(ScheduledTransactionsCardComponent, {
  TransactionRow,
  NoTransactionsRow,
});
